/**
 * WeightOfTheWorldSkillEffect — Grit ironbody-path apotheosis: area slow field.
 *
 * Channel for 1.5 seconds. On release: 5 slow stacks hit every enemy
 * within 4 tiles. Each stack cuts movement and attack speed by 4%
 * (20% total at max). Full stacks also reduce enemy damage output by
 * 15%. Stacks decay after 8 seconds without a Grit hit to refresh.
 *
 * Phases: windup (1500ms channel) → active (instant field burst) →
 * recovery → cooldown.
 *
 * Extends PhasedSkillEffect for phase lifecycle.
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see grit-spec.md § Weight of the World
 */

import { PhasedSkillEffect, type SkillPhase } from '../../PhasedSkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface WeightOfTheWorldDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
  };
  readonly statusEffects: {
    apply(statusId: string, ...args: unknown[]): void;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface WeightOfTheWorldConfig {
  readonly windupMs: number;
  readonly recoveryMs: number;
  readonly cooldownMs: number;
  readonly radiusTiles: number;
  readonly slowStacks: number;
  readonly slowPerStack: number;
  readonly fullStackDamageReduction: number;
  readonly stackDecayMs: number;
}

const DEFAULT_CONFIG: WeightOfTheWorldConfig = {
  windupMs: 1500,                      // 1.5s channel
  recoveryMs: 200,
  cooldownMs: 4000,                    // very_long cooldown tier
  radiusTiles: 4,
  slowStacks: 5,
  slowPerStack: 0.04,                 // 4% per stack
  fullStackDamageReduction: 0.15,     // 15% enemy damage reduction
  stackDecayMs: 8000,                 // 8s decay without refresh
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class WeightOfTheWorldSkillEffect extends PhasedSkillEffect {
  readonly skillId = 'weight_of_the_world';

  protected readonly phaseSequence: readonly SkillPhase[] =
    ['windup', 'active', 'recovery', 'cooldown'];

  private readonly deps: WeightOfTheWorldDeps;
  private readonly config: WeightOfTheWorldConfig;

  // Activation state
  private _fieldFired = false;

  constructor(deps: WeightOfTheWorldDeps, config: Partial<WeightOfTheWorldConfig> = {}) {
    super();
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get fieldFired(): boolean { return this._fieldFired; }
  get radiusTiles(): number { return this.config.radiusTiles; }
  get slowStacks(): number { return this.config.slowStacks; }
  get slowPerStack(): number { return this.config.slowPerStack; }
  get totalSlowPercent(): number { return this.config.slowStacks * this.config.slowPerStack; }
  get fullStackDamageReduction(): number { return this.config.fullStackDamageReduction; }
  get stackDecayMs(): number { return this.config.stackDecayMs; }

  // --- Completion override ---

  /**
   * The base PhasedSkillEffect marks _complete when leaving the penultimate
   * phase (recovery → cooldown), but Weight of the World has a meaningful
   * 4-second cooldown. Override to report complete only after all phases
   * finish and the skill returns to idle.
   */
  isComplete(): boolean {
    return this.phase === 'idle' && this._fieldFired;
  }

  // --- PhasedSkillEffect interface ---

  protected getPhaseDuration(phase: SkillPhase): number {
    switch (phase) {
      case 'windup': return this.config.windupMs;
      case 'active': return 0;  // instant field burst
      case 'recovery': return this.config.recoveryMs;
      case 'cooldown': return this.config.cooldownMs;
      default: return 0;
    }
  }

  protected onActivate(): void {
    this._fieldFired = false;
  }

  protected onPhaseEnter(phase: SkillPhase): void {
    if (phase === 'active') {
      this.fireField();
    }
  }

  // --- Internal ---

  private fireField(): void {
    // Apply 5 stacks of weight_slow to all enemies in radius
    this.deps.statusEffects.apply(
      'weight_slow',
      this.config.stackDecayMs,
      {
        stacks: this.config.slowStacks,
        slowPerStack: this.config.slowPerStack,
        radiusTiles: this.config.radiusTiles,
      },
      'weight_of_the_world',
    );

    // Apply damage reduction debuff to all full-stack enemies
    this.deps.statusEffects.apply(
      'weight_damage_reduction',
      this.config.stackDecayMs,
      {
        damageReduction: this.config.fullStackDamageReduction,
        radiusTiles: this.config.radiusTiles,
      },
      'weight_of_the_world',
    );

    this._fieldFired = true;
  }
}
